import React, { useCallback, useLayoutEffect, useState, useEffect } from 'react';
import ProductVariationContext from './ProductVariationContext';
import { useLocation } from 'react-router-dom';
import { getDefaultValuesFromUrl } from './getDefaultValuesFromUrl';
import logWarningInDev from '../../helpers/logWarningInDev';
export function peekVariant(product, selectedOptions) {
    const values = product.variants.options.map(({ name }) => selectedOptions[name]);
    return product.variants.values.find((variation) => {
        return variation.values
            ? values.join(',') === variation.values.join(',')
            : false;
    });
}
function getSelectOptionState(optionName, value, state, product) {
    let newSelectedOptions = Object.assign(Object.assign({}, state.selectedOptions), { [optionName]: value });
    const selectedVariation = peekVariant(product, newSelectedOptions);
    // If all options are selected but there is no valid variation only choose the selected option and clear the others.
    const validSelectedOptions = selectedVariation ||
        product.variants.options.length !== Object.keys(newSelectedOptions).length;
    if (!validSelectedOptions) {
        newSelectedOptions = {
            [optionName]: value
        };
    }
    const hasMissingOption = getMissingOptions(product, newSelectedOptions).length !== 0;
    const newState = {
        selectedOptions: newSelectedOptions,
        missingOption: null,
        hasMissingOption,
        selectedVariation
    };
    return Object.assign(Object.assign({}, state), newState);
}
function getInitialState(product, location) {
    const initialState = {
        quantity: 1,
        selectedVariation: null,
        product,
        selectedOptions: {},
        options: product.variants.options,
        missingOption: null,
        missingOptions: [],
        hasMissingOption: product.variants.options.length > 0
    };
    const optionsWithOneValue = product.variants.options.filter(option => option.values.length === 1);
    let partialState = {};
    for (const option of optionsWithOneValue) {
        partialState = getSelectOptionState(option.name, option.values[0], initialState, product);
    }
    const defaultValuesFromUrl = getDefaultValuesFromUrl(product, location);
    return Object.assign(Object.assign(Object.assign({}, initialState), partialState), defaultValuesFromUrl);
}
function getMissingOption(product, selectedOptions) {
    return product.variants.options.find(option => {
        return !Object.keys(selectedOptions).includes(option.name);
    });
}
export function getMissingOptions(product, selectedOptions) {
    return product.variants.options.filter(option => {
        return !Object.keys(selectedOptions).includes(option.name);
    });
}
function isDisabled(optionName, value, state, product) {
    const newSelectedOptions = Object.assign(Object.assign({}, state.selectedOptions), { [optionName]: value });
    // If the number of options is not equal to the selected options then it should always be valid
    // If it is equal, we want to make sure there is a valid variant for those options
    const validSelectedOptions = state.options.length !== Object.keys(newSelectedOptions).length ||
        peekVariant(product, newSelectedOptions);
    // Finally, even if there is no valid variant, we never want the first row of options to be disabled
    return !(state.options[0].name === optionName || validSelectedOptions);
}
function getSelectable({ state, setState, product }) {
    return function getSelectableOptionItems(optionName) {
        const { selectedOptions, options } = state;
        const picker = options.find(option => option.name === optionName);
        return (picker &&
            picker.values.map((value) => {
                const selected = selectedOptions[optionName] === value;
                const variant = peekVariant(product, Object.assign(Object.assign({}, state.selectedOptions), { [optionName]: value }));
                const disabled = isDisabled(optionName, value, state, product);
                return {
                    props: {
                        disabled,
                        selected,
                        onClick: () => {
                            setState(state => getSelectOptionState(optionName, value, state, product));
                        }
                    },
                    value,
                    variant
                };
            }));
    };
}
function setMissing({ product, state, setState }) {
    return function () {
        const missingOption = getMissingOption(product, state.selectedOptions);
        const missingOptions = getMissingOptions(product, state.selectedOptions);
        // We provide the missing option purely for backwards compatibility where before we were not return an array of options
        setState(state => (Object.assign(Object.assign({}, state), { missingOption,
            missingOptions, hasMissingOption: true })));
        return missingOptions;
    };
}
////////////////////////////////////////////////////////////////////////////////
// Variation Provider component
const ProductVariationProvider = ({ product, children }) => {
    const location = useLocation();
    const [variationState, setVariationState] = useState(getInitialState(product, location));
    useEffect(() => {
        logWarningInDev(true, `**** ProductVariationProvider has been deprecated. This component will be removed in Flight 5.0.0. Please use the useProductVariants hook, as described at https://docs.dev.jetshop.se/useProductVariants`);
    }, []);
    // Abuse useLayoutEffect for now.
    // When this provider loads, it's initially loaded with a mock product with
    // fake data and no variants. (this is a bad idea and we should fix it).
    // So when setting the initial state above, the variants are empty.
    // Then, the product query returns, and we have a product! So, previously we
    // used componentDidUpdate, which would re-render this component with the new
    // product (and thus the updated variants).
    // componentDidUpdate happens *before paint*. This means that you never see
    // the version of the component with no variants.
    // useLayoutEffect also happens *before paint*, so this is equivalent.
    // useEffect will happen *after paint*, which means you see a product page
    // with no variants, and then the variants pop in.
    // The real solution to this should be a bigger refactor where we just don't
    // feed a fake product in to this component.
    useLayoutEffect(() => {
        setVariationState(getInitialState(product, location));
    }, [product, location]);
    const getSelectableOptionItems = useCallback(getSelectable({
        state: variationState,
        setState: setVariationState,
        product
    }), [product, variationState]);
    const setMissingOption = useCallback(setMissing({ state: variationState, setState: setVariationState, product }), [product, variationState]);
    const contextValues = React.useMemo(() => {
        return Object.assign(Object.assign({}, variationState), { product,
            getSelectableOptionItems,
            setMissingOption });
    }, [getSelectableOptionItems, product, setMissingOption, variationState]);
    return (React.createElement(ProductVariationContext.Provider, { value: contextValues }, children));
};
export default ProductVariationProvider;
//# sourceMappingURL=ProductVariationProvider.js.map